import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon.jsx";
import { useChromeSlots } from "./chrome.js";

/*
 * A page declares its topbar contribution by rendering this once:
 *
 *   <PageChrome breadcrumb={["Users"]} />
 *
 *   <PageChrome
 *       breadcrumb={[{ label: "Dashboard", to: "/dashboard" }, user.name]}
 *       title={user.name}
 *   >
 *       <AutoRefresh interval={120_000} />   // topbar actions
 *   </PageChrome>
 *
 * breadcrumb items are strings or { label, to }. The last one is always the
 * current page and is never a link. `title` overrides the document title, which
 * otherwise comes from the last crumb.
 */
export default function PageChrome({ breadcrumb = [], title, children }) {
    const { crumbs, actions } = useChromeSlots();

    const items = breadcrumb.map((c) => (typeof c === "string" ? { label: c } : c));
    const last = items[items.length - 1];
    const docTitle = title ?? last?.label;

    useEffect(() => {
        if (!docTitle) return;
        const previous = document.title;
        document.title = `${docTitle} · Performance Track`;
        return () => {
            document.title = previous;
        };
    }, [docTitle]);

    return (
        <>
            {crumbs &&
                createPortal(
                    items.map((item, i) => (
                        <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
                            {i > 0 && <Icon name="chevronRight" className="size-3.5" />}
                            {i === items.length - 1 ? (
                                <span className="font-semibold text-slate-900 dark:text-slate-100">
                                    {item.label}
                                </span>
                            ) : item.to ? (
                                <Link
                                    to={item.to}
                                    className="hover:text-slate-600 dark:hover:text-slate-300"
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <span>{item.label}</span>
                            )}
                        </span>
                    )),
                    crumbs,
                )}

            {children && actions && createPortal(children, actions)}
        </>
    );
}
